import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { ArrowLeft, Globe, Bank, Buildings, UsersThree, Coins, GlobeHemisphereWest } from "@phosphor-icons/react";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "MR", nom: "Mauritanie" },
  { code: "SN", nom: "Sénégal" },
  { code: "ML", nom: "Mali" },
  { code: "BF", nom: "Burkina Faso" },
  { code: "NE", nom: "Niger" },
  { code: "CI", nom: "Côte d'Ivoire" },
  { code: "GH", nom: "Ghana" },
  { code: "NG", nom: "Nigeria" },
  { code: "BJ", nom: "Bénin" },
  { code: "TG", nom: "Togo" },
  { code: "GN", nom: "Guinée" },
  { code: "SL", nom: "Sierra Leone" },
  { code: "LR", nom: "Liberia" },
  { code: "GM", nom: "Gambie" },
  { code: "CV", nom: "Cabo Verde" },
  { code: "GW", nom: "Guinée-Bissau" },
];

const UEMOA_MEMBERS = ["BJ", "BF", "CI", "GW", "ML", "NE", "SN", "TG"];

export default function ObservatoryData() {
  const [iso, setIso] = useState("MR");
  const [wb, setWb] = useState(null);
  const [imf, setImf] = useState(null);
  const [hdr, setHdr] = useState(null);
  const [uemoa, setUemoa] = useState(null);
  const [zlecaf, setZlecaf] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(iso); }, [iso]);

  const isMember = UEMOA_MEMBERS.includes(iso);

  const load = async (code) => {
    setLoading(true);
    setWb(null); setImf(null); setHdr(null); setUemoa(null); setZlecaf(null);
    try {
      const member = UEMOA_MEMBERS.includes(code);
      const calls = [
        api.get(`/observatory/data/wb/${code}`).catch(() => ({ data: null })),
        api.get(`/observatory/data/imf/${code}`).catch(() => ({ data: null })),
        api.get(`/observatory/data/hdr/${code}`).catch(() => ({ data: null })),
        api.get(`/observatory/data/zlecaf/${code}`).catch(() => ({ data: null })),
        member
          ? api.get(`/observatory/data/uemoa/${code}`).catch(() => ({ data: null }))
          : api.get(`/observatory/data/uemoa`).catch(() => ({ data: null })),
      ];
      const [r1, r2, r3, r4, r5] = await Promise.all(calls);
      setWb(r1.data); setImf(r2.data); setHdr(r3.data); setZlecaf(r4.data); setUemoa(r5.data);
    } catch (e) { toast.error("Erreur API officielle"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <Link to="/observatory" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-[var(--signal)]" data-testid="data-back">
        <ArrowLeft size={14} /> Retour à la veille
      </Link>

      <div className="mb-8 fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">§ Vita-Observatory · Sources officielles</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight flex items-center gap-3">
            <Globe size={40} className="text-[var(--signal)]" /> Catalogue de données
          </h1>
          <p className="mt-3 text-[var(--muted)] max-w-2xl">Données macroéconomiques live des API officielles World Bank DataBank et FMI DataMapper. Pas d'IA — chiffres bruts vérifiables à la source.</p>
        </div>
        <div>
          <div className="label-mono mb-1">Pays</div>
          <select value={iso} onChange={(e) => setIso(e.target.value)}
            className="border border-black px-4 py-2 bg-transparent min-w-[12rem]" data-testid="data-country-select">
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.nom}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="border border-black/15 p-12 text-center label-mono">Récupération auprès de WB + FMI...</div>}

      {/* WB */}
      <section className="mb-8 border border-black/15 bg-[var(--paper)]">
        <div className="border-b border-black/15 p-5 flex items-center gap-3">
          <Buildings size={22} className="text-[var(--signal)]" />
          <div>
            <div className="font-serif text-2xl">World Bank · DataBank</div>
            <div className="font-mono text-xs text-[var(--muted)]">api.worldbank.org/v2 · indicateurs WDI</div>
          </div>
        </div>
        {wb && Object.keys(wb.indicators || {}).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15">
            {Object.entries(wb.indicators).map(([code, val]) => (
              <IndicatorCard key={code} code={code} {...val} />
            ))}
          </div>
        ) : (
          <div className="p-8 label-mono text-center text-[var(--muted)]">{loading ? "" : "Aucune donnée WB pour ce pays"}</div>
        )}
      </section>

      {/* IMF */}
      <section className="border border-black/15 bg-[var(--paper)] mb-8">
        <div className="border-b border-black/15 p-5 flex items-center gap-3">
          <Bank size={22} className="text-[var(--signal)]" />
          <div>
            <div className="font-serif text-2xl">FMI · DataMapper</div>
            <div className="font-mono text-xs text-[var(--muted)]">imf.org/external/datamapper · WEO + Fiscal Monitor</div>
          </div>
        </div>
        {imf && Object.keys(imf.indicators || {}).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15">
            {Object.entries(imf.indicators).map(([code, val]) => (
              <IndicatorCard key={code} code={code} {...val} />
            ))}
          </div>
        ) : (
          <div className="p-8 label-mono text-center text-[var(--muted)]">{loading ? "" : "Aucune donnée FMI pour ce pays"}</div>
        )}
      </section>

      {/* HDR */}
      <section className="border border-black/15 bg-[var(--paper)] mb-8" data-testid="data-hdr-section">
        <div className="border-b border-black/15 p-5 flex items-center gap-3">
          <UsersThree size={22} className="text-[var(--signal)]" />
          <div>
            <div className="font-serif text-2xl">PNUD · Développement humain</div>
            <div className="font-mono text-xs text-[var(--muted)]">{hdr?.source || "Indicateurs HDR-PNUD"} · santé · éducation · inégalités</div>
          </div>
        </div>
        {hdr && Object.keys(hdr.indicators || {}).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15">
            {Object.entries(hdr.indicators).map(([code, val]) => (
              <IndicatorCard key={code} code={code} {...val} />
            ))}
          </div>
        ) : (
          <div className="p-8 label-mono text-center text-[var(--muted)]">{loading ? "" : "Aucune donnée HDR pour ce pays"}</div>
        )}
      </section>

      {/* BCEAO / UEMOA */}
      <section className="border border-black/15 bg-[var(--paper)] mb-8" data-testid="data-uemoa-section">
        <div className="border-b border-black/15 p-5 flex items-center gap-3 flex-wrap">
          <Coins size={22} className="text-[var(--signal)]" />
          <div className="flex-1 min-w-0">
            <div className="font-serif text-2xl">
              BCEAO · {isMember ? `${COUNTRIES.find(c => c.code === iso)?.nom || iso} · UEMOA` : "Agrégat zone UEMOA"}
            </div>
            <div className="font-mono text-xs text-[var(--muted)]">
              {isMember
                ? "Indicateurs monétaires & fiscaux (via WB) · membre UEMOA"
                : `Moyenne 8 États membres : Bénin, Burkina Faso, Côte d'Ivoire, Guinée-Bissau, Mali, Niger, Sénégal, Togo`}
            </div>
          </div>
          {uemoa?.bulletin_url && (
            <a
              href={uemoa.bulletin_url}
              target="_blank"
              rel="noreferrer"
              className="border border-black px-3 py-1.5 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2"
              data-testid="data-uemoa-bulletin-link"
            >
              Bulletin BCEAO ↗
            </a>
          )}
        </div>
        {uemoa && Object.keys(uemoa.indicators || {}).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15">
            {Object.entries(uemoa.indicators).map(([code, val]) => (
              <IndicatorCard key={code} code={code} {...val} />
            ))}
          </div>
        ) : (
          <div className="p-8 label-mono text-center text-[var(--muted)]">{loading ? "" : "Aucune donnée UEMOA disponible"}</div>
        )}
      </section>

      {/* ZLECAf / Afreximbank */}
      <section className="border border-black/15 bg-[var(--paper)]" data-testid="data-zlecaf-section">
        <div className="border-b border-black/15 p-5 flex items-center gap-3 flex-wrap">
          <GlobeHemisphereWest size={22} className="text-[var(--signal)]" />
          <div className="flex-1 min-w-0">
            <div className="font-serif text-2xl">ZLECAf · Commerce intra-africain</div>
            <div className="font-mono text-xs text-[var(--muted)]">
              Cadre Afreximbank · structure des exports · IDE · intégration régionale
            </div>
          </div>
          {zlecaf?.afreximbank_url && (
            <a
              href={zlecaf.afreximbank_url}
              target="_blank"
              rel="noreferrer"
              className="border border-black px-3 py-1.5 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2"
              data-testid="data-zlecaf-afrexim-link"
            >
              Publications Afreximbank ↗
            </a>
          )}
        </div>
        {zlecaf && Object.keys(zlecaf.indicators || {}).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15">
            {Object.entries(zlecaf.indicators).map(([code, val]) => (
              <IndicatorCard key={code} code={code} {...val} />
            ))}
          </div>
        ) : (
          <div className="p-8 label-mono text-center text-[var(--muted)]">{loading ? "" : "Aucune donnée ZLECAf pour ce pays"}</div>
        )}
      </section>
    </div>
  );
}

function IndicatorCard({ code, label, series, error }) {
  const last = series && series.length ? series[series.length - 1] : null;
  return (
    <div className="bg-[var(--paper)] p-5">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="label-mono text-[10px]">{code}</div>
          <div className="font-serif text-lg mt-0.5">{label}</div>
        </div>
        {last && (
          <div className="text-right">
            <div className="text-3xl font-light tabular-nums">{Number(last.valeur).toFixed(2)}</div>
            <div className="font-mono text-xs text-[var(--muted)]">{last.annee}</div>
          </div>
        )}
      </div>
      {series && series.length > 1 ? (
        <div className="mt-3 h-32">
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <XAxis dataKey="annee" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} />
              <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #000", borderRadius: 0, fontSize: 11 }} />
              <Line type="monotone" dataKey="valeur" stroke="#D92D20" strokeWidth={1.5} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-3 h-32 flex items-center justify-center text-xs font-mono text-[var(--muted)]">
          {error ? "API indisponible" : "Pas de série temporelle"}
        </div>
      )}
    </div>
  );
}
